import React from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    TextField,
    Button,
    Container,
    Typography,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material';
import {useFormik} from 'formik';
import * as yup from 'yup';
import {useTeam, useUpdateTeam} from '~/apollo/hooks-team';

interface TeamFormData {
    name: string;
    description: string;
    organizationId: string;
    active: boolean;
}

const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    description: yup.string(),
    organizationId: yup.string(),
    active: yup.boolean(),
});

const TeamEditPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {team, loading: teamLoading, error: teamError} = useTeam(id ?? '1');
    const {updateTeam, loading: updateLoading, error: updateError, errors: updateErrors} = useUpdateTeam();

    const [submissionError, setSubmissionError] = React.useState<string | null>(null);

    const initialValues: TeamFormData = {
        name: team?.name || '',
        description: team?.description || '',
        organizationId: team?.organizationId || '',
        active: team?.active || false,
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setSubmitting,
    } = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setSubmitting(true);
            setSubmissionError(null);
            try {
                await updateTeam(id!, {
                    name: values.name,
                    description: values.description,
                    active: values.active,
                });
                const errorCnt = (updateErrors?.length ?? 0)
                if (!updateError && errorCnt === 0) {
                    navigate('/teams');
                } else if (errorCnt > 0) {
                    if (updateErrors) {
                        setSubmissionError(updateErrors[0]?.message);
                    }
                } else if (updateError) {
                    setSubmissionError(updateError.message);
                }
            } catch (err: any) {
                setSubmissionError(err.message);
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (teamLoading) {
        return (
            <Container>
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px'}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (teamError) {
        return (
            <Container>
                <Alert severity="error">Error loading team: {teamError.message}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Typography variant="h4" gutterBottom>
                Edit Team
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    fullWidth
                    margin="normal"
                    required
                />
                <TextField
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    fullWidth
                    margin="normal"
                    required
                />

                <FormControlLabel
                    control={<Checkbox name="active" checked={values.active} onChange={handleChange}/>}
                    label="Active"
                />

                <Box sx={{mt: 2}}>
                    <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                        {isSubmitting ? <CircularProgress size={24} color="inherit"/> : 'Save Changes'}
                    </Button>
                    <Button onClick={() => navigate('/teams')} sx={{ml: 2}}>
                        Cancel
                    </Button>
                </Box>

                {submissionError && (
                    <Alert severity="error" sx={{mt: 2}}>
                        Error updating team: {submissionError}
                    </Alert>
                )}
            </form>
        </Container>
    );
};

export default TeamEditPage;